import { Field, FieldLabel, FieldSelect } from '@ibe/ui';
import { useInfiniteQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { parseOrThrow } from '../../lib/api/contract.js';
import { useApi } from '../../lib/api/api-context.js';
import { useApiList } from '../../lib/api/hooks.js';

const propertyRow = z.looseObject({
  id: z.string(),
  name: z.string(),
  displayName: z.string().or(z.null()).optional(),
});

const propertySetupRow = z.looseObject({
  id: z.string(),
  name: z.string(),
  code: z.string(),
});

const propertyListSchema = z.object({
  items: z.array(propertyRow),
  pagination: z.object({
    page: z.number(),
    pageSize: z.number(),
    totalItems: z.number(),
    totalPages: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  }),
});

const _propertyOptionsSchema = z.array(z.object({ value: z.string(), label: z.string() }));

const OPTIONS_PAGE_SIZE = 100;

/**
 * Active properties as a dropdown vocabulary.
 *
 * Returns the first page only. Use this when the caller only needs a quick
 * picker and does not own its own pagination.
 */
export function usePropertyOptions(): UseQueryResult<z.infer<typeof _propertyOptionsSchema>> {
  const query = useApiList({
    resource: 'properties',
    path: '/v1/properties',
    schema: propertyRow,
    params: {
      page: 1,
      pageSize: OPTIONS_PAGE_SIZE,
      scope: { status: 'ACTIVE' },
      fields: ['id', 'name', 'displayName'],
    },
  });

  const data = useMemo(() => {
    if (query.data === undefined) return undefined;
    return query.data.items.map((row) => ({
      value: row.id,
      label: row.displayName ?? row.name,
    }));
  }, [query.data]);

  return { ...query, data } as unknown as UseQueryResult<z.infer<typeof _propertyOptionsSchema>>;
}

/**
 * Active properties formatted the way the property-setup picker does,
 * with `name (code)` labels.
 *
 * Returns the first page only. Shared so other modules do not import the
 * property-setup module directly.
 */
export function usePropertySetupOptions(): UseQueryResult<z.infer<typeof _propertyOptionsSchema>> {
  const query = useApiList({
    resource: 'properties',
    path: '/v1/properties',
    schema: propertySetupRow,
    params: {
      page: 1,
      pageSize: OPTIONS_PAGE_SIZE,
      scope: { status: 'ACTIVE' },
      fields: ['id', 'name', 'code'],
    },
  });

  const data = useMemo(() => {
    if (query.data === undefined) return undefined;
    return query.data.items.map((row) => ({
      value: row.id,
      label: `${row.name} (${row.code})`,
    }));
  }, [query.data]);

  return { ...query, data } as unknown as UseQueryResult<z.infer<typeof _propertyOptionsSchema>>;
}

export interface PropertySelectProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string | undefined;
  readonly required?: boolean;
  readonly readOnly?: boolean;
  readonly description?: string | undefined;
  /** Suppress the default property-select description. Useful in list filter bars. */
  readonly hideDescription?: boolean;
}

export function PropertySelect({
  value,
  onChange,
  error,
  required = false,
  readOnly = false,
  description,
  hideDescription = false,
}: PropertySelectProps): React.JSX.Element {
  const api = useApi();

  const query = useInfiniteQuery({
    queryKey: [
      'properties',
      'select',
      'infinite',
      { status: 'ACTIVE', pageSize: OPTIONS_PAGE_SIZE },
    ],
    queryFn: async ({ pageParam }) => {
      const result = await api.list<unknown>('/v1/properties', {
        page: String(pageParam),
        pageSize: String(OPTIONS_PAGE_SIZE),
        status: 'ACTIVE',
        fields: 'id,name,displayName',
      });
      return parseOrThrow(propertyListSchema, result, '/v1/properties');
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : undefined,
  });

  const options = useMemo(() => {
    return (
      query.data?.pages.flatMap((page) =>
        page.items.map((row) => ({
          value: row.id,
          label: row.displayName ?? row.name,
        })),
      ) ?? []
    );
  }, [query.data]);

  const defaultDescription = hideDescription
    ? undefined
    : readOnly
      ? 'Fixed once the policy exists — moving it would re-scope every rate plan that references it.'
      : 'Only properties you manage are offered.';

  const handleEndReached = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
  }, [query]);

  return (
    <Field error={error} required={required} description={description ?? defaultDescription}>
      <FieldLabel>Property</FieldLabel>
      <FieldSelect
        value={value}
        onValueChange={onChange}
        options={options}
        placeholder="Choose a property…"
        disabled={readOnly || query.isPending}
        searchable
        onEndReached={handleEndReached}
        hasNextPage={query.hasNextPage}
      />
    </Field>
  );
}
